// Field incident / hazard report submitted from the shelter portal.
// Reports are persisted to the shelterIncidents table and cached locally.

import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../../../store/authStore';
import { notifications } from '../../../lib/notifications';
import { INCIDENT_REPORT_TYPES } from '../../../types/shelter';
import type { Shelter } from '../../../types/shelter';
import { IconCheck, IconAlertTriangle, IconHistory, IconSearch, IconX } from '@tabler/icons-react';
import { queryRecords, createRecord } from '../../../api/client';
import { TABLES } from '../../../api/tables';
import { FIELDS } from '../../../api/fields';
import { formatPhone } from '../../../lib/phoneFormat';

interface Props { shelter: Shelter; }

interface FieldReport {
  id: string;
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  location: string;
  involvedPartyIds: string;
  involvedNames: string;
  submittedBy: string;
  timestamp: string;
}

interface PersonResult {
  id: string;
  name: string;
  phone: string;
}

// Emoji decoration for mobile readability
const TYPE_EMOJI: Record<string, string> = {
  medical:    '🏥',
  behavioral: '⚡',
  security:   '🔒',
  maintenance:'🏗',
  theft:      '⚠️',
  other:      '📋',
};

const SEVERITIES: { value: FieldReport['severity']; label: string; color: string; bg: string }[] = [
  { value: 'low',      label: 'Low',      color: '#16a34a', bg: '#dcfce7' },
  { value: 'medium',   label: 'Medium',   color: '#92400e', bg: '#fef3c7' },
  { value: 'high',     label: 'High',     color: '#B12525', bg: '#fee2e2' },
  { value: 'critical', label: 'Critical', color: '#7c3aed', bg: '#ede9fe' },
];

export default function ShelterPortalReport({ shelter }: Props) {
  const { user } = useAuthStore();
  const LOG_KEY = `sp-reports-${shelter.id}`;

  const [reports, setReports] = useState<FieldReport[]>(() => {
    try { return JSON.parse(localStorage.getItem(LOG_KEY) || '[]'); } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem(LOG_KEY, JSON.stringify(reports.slice(0, 100))); } catch { /* ignore */ }
  }, [reports, LOG_KEY]);

  const [type,        setType]        = useState('');
  const [severity,    setSeverity]    = useState<FieldReport['severity']>('medium');
  const [description, setDescription] = useState('');
  const [location,    setLocation]    = useState(
    [shelter.address, shelter.city].filter(Boolean).join(', ')
  );
  const [involvedPeople, setInvolvedPeople] = useState<PersonResult[]>([]);
  const [peopleSearch,   setPeopleSearch]   = useState('');
  const [peopleResults,  setPeopleResults]  = useState<PersonResult[]>([]);
  const [searching,      setSearching]      = useState(false);
  const searchRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saving, setSaving]   = useState(false);
  const [done, setDone]       = useState(false);
  const [showLog, setShowLog] = useState(false);

  const handlePeopleSearch = (q: string) => {
    setPeopleSearch(q);
    if (searchRef.current) clearTimeout(searchRef.current);
    if (!q.trim()) { setPeopleResults([]); return; }
    searchRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await queryRecords(TABLES.people, { search: q.trim(), limit: 8 });
        const fp = FIELDS.people;
        setPeopleResults(r.records.map((row: Record<string, unknown>) => ({
          id: String(row.record_id),
          name: `${row[fp.firstName] ?? ''} ${row[fp.lastName] ?? ''}`.trim(),
          phone: String(row[fp.phone] ?? ''),
        })));
      } catch { /* ignore */ }
      finally { setSearching(false); }
    }, 300);
  };

  const addPerson = (p: PersonResult) => {
    if (!involvedPeople.find(x => x.id === p.id)) {
      setInvolvedPeople(prev => [...prev, p]);
    }
    setPeopleSearch('');
    setPeopleResults([]);
  };

  const removePerson = (id: string) => setInvolvedPeople(prev => prev.filter(p => p.id !== id));

  const submittedBy = (user as any)?.name || (user as any)?.email || 'Field Worker';

  const handleSubmit = async () => {
    if (!type) {
      notifications.show({ title: 'Required', message: 'Select a report type.', color: 'var(--color-error)' });
      return;
    }
    if (!description.trim()) {
      notifications.show({ title: 'Required', message: 'Please describe the incident.', color: 'var(--color-error)' });
      return;
    }
    setSaving(true);
    const fi = FIELDS.shelterIncidents;
    const notes = [
      location.trim() ? `Location: ${location.trim()}` : '',
      involvedPeople.length > 0 ? `Involved: ${involvedPeople.map(p => p.name).join(', ')}` : '',
    ].filter(Boolean).join('\n');
    try {
      await createRecord(TABLES.shelterIncidents, {
        [fi.shelterId]:   String(shelter.id),
        [fi.reportType]:  type,
        [fi.description]: description.trim(),
        [fi.severity]:    severity,
        [fi.reportedBy]:  submittedBy,
        [fi.reportedAt]:  new Date().toISOString(),
        ...(notes ? { [fi.notes]: notes } : {}),
      });
      const report: FieldReport = {
        id: `${Date.now()}`,
        type,
        severity,
        description: description.trim(),
        location: location.trim(),
        involvedPartyIds: JSON.stringify(involvedPeople.map(p => p.id)),
        involvedNames: involvedPeople.map(p => p.name).join(', '),
        submittedBy,
        timestamp: new Date().toLocaleString(),
      };
      setReports(prev => [report, ...prev]);
      setDone(true);
      setTimeout(() => {
        setDone(false);
        setType('');
        setSeverity('medium');
        setDescription('');
        setLocation([shelter.address, shelter.city].filter(Boolean).join(', '));
        setInvolvedPeople([]);
        setPeopleSearch('');
      }, 2000);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to submit report.', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 16px' }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', background: '#dcfce7',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
        }}>
          <IconCheck size={32} color="#16a34a" />
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#15803d', marginBottom: 8 }}>Report Submitted</div>
        <div style={{ fontSize: 14, color: '#7B7D7F' }}>Your report has been logged and flagged for review.</div>
      </div>
    );
  }

  const sev = SEVERITIES.find(s => s.value === severity)!;

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: '#fef3c7', border: '1px solid #fde68a',
        borderRadius: 4, padding: '8px 16px', marginBottom: 16, fontSize: 14, color: '#92400e',
      }}>
        <IconAlertTriangle size={16} />
        <span>Use this form to report hazards, incidents, or urgent needs at this shelter.</span>
      </div>

      {/* Type */}
      <div className="sp-card">
        <div className="sp-card-title">Incident Type</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {INCIDENT_REPORT_TYPES.map(t => (
            <button
              key={t.value}
              onClick={() => setType(t.value)}
              style={{
                padding: '8px 16px', textAlign: 'left', borderRadius: 4,
                border: `1.5px solid ${type === t.value ? '#4B3FFF' : '#e2e8f0'}`,
                background: type === t.value ? '#eeeeff' : 'white',
                color: type === t.value ? '#2C01D6' : '#1B1C1C',
                fontWeight: type === t.value ? 700 : 500,
                fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              <span style={{ fontSize: 20 }}>{TYPE_EMOJI[t.value] ?? '📋'}</span>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Severity */}
      <div className="sp-card">
        <div className="sp-card-title">Severity</div>
        <div className="sp-severity-row">
          {SEVERITIES.map(s => (
            <button
              key={s.value}
              className="sp-severity-chip"
              onClick={() => setSeverity(s.value)}
              style={{
                background: severity === s.value ? s.bg : 'white',
                color: severity === s.value ? s.color : '#7B7D7F',
                borderColor: severity === s.value ? s.color : '#e2e8f0',
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
        {/* Severity description hint */}
        <div style={{ marginTop: 8, fontSize: 12, color: '#939598' }}>
          {severity === 'low' && 'Non-urgent, can be addressed during normal operations.'}
          {severity === 'medium' && 'Needs attention within the next hour.'}
          {severity === 'high' && 'Requires immediate supervisor notification.'}
          {severity === 'critical' && 'Life safety threat — call 911 and notify EOC immediately.'}
        </div>
      </div>

      {/* Description */}
      <div className="sp-card">
        <div className="sp-card-title">Description</div>
        <div className="sp-field">
          <label className="sp-label">What happened? <span className="sp-req">*</span></label>
          <textarea
            className="sp-textarea"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Describe the incident clearly — who, what, where, when…"
            style={{ minHeight: 100 }}
          />
        </div>
        <div className="sp-field" style={{ marginBottom: 0 }}>
          <label className="sp-label">Specific Location</label>
          <input
            className="sp-input"
            value={location}
            onChange={e => setLocation(e.target.value)}
            placeholder="Wing, room, cot area…"
          />
        </div>
      </div>

      {/* Parties Involved */}
      <div className="sp-card">
        <div className="sp-card-title">Parties Involved</div>
        <div style={{ fontSize: 12, color: '#939598', marginBottom: 8 }}>
          Search the People Directory to link individuals involved in this incident.
        </div>

        {/* Selected chips */}
        {involvedPeople.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            {involvedPeople.map(p => (
              <div key={p.id} style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                background: '#eeeeff', border: '1.5px solid #4B3FFF',
                borderRadius: 99, padding: '3px 10px 3px 8px',
                fontSize: 12, fontWeight: 600, color: '#2C01D6',
              }}>
                {p.name}
                <button
                  onClick={() => removePerson(p.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: '#4B3FFF', display: 'flex', alignItems: 'center' }}
                >
                  <IconX size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Search input */}
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'relative' }}>
            <IconSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#939598', pointerEvents: 'none' }} />
            <input
              className="sp-input"
              style={{ paddingLeft: 32 }}
              value={peopleSearch}
              onChange={e => handlePeopleSearch(e.target.value)}
              placeholder="Search by name…"
            />
          </div>
          {searching && (
            <div style={{ fontSize: 12, color: '#939598', marginTop: 4 }}>Searching…</div>
          )}
          {peopleResults.length > 0 && (
            <div style={{
              background: 'white', border: '1px solid #e2e8f0', borderRadius: 4,
              boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)', marginTop: 4, overflow: 'hidden',
            }}>
              {peopleResults.map(p => (
                <button
                  key={p.id}
                  onClick={() => addPerson(p)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '8px 16px', border: 'none', background: 'none',
                    cursor: 'pointer', fontFamily: 'inherit', fontSize: 14,
                    borderBottom: '1px solid #f1f5f9',
                  }}
                  onTouchStart={e => (e.currentTarget.style.background = '#f5f5ff')}
                  onTouchEnd={e => (e.currentTarget.style.background = 'none')}
                >
                  <span style={{ fontWeight: 600, color: '#1B1C1C' }}>{p.name}</span>
                  {p.phone && <span style={{ color: '#939598', fontSize: 12, marginLeft: 8 }}>{formatPhone(p.phone)}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Submitter info */}
      <div style={{ fontSize: 12, color: '#939598', marginBottom: 16, textAlign: 'center' }}>
        Submitting as <strong style={{ color: '#475569' }}>{submittedBy}</strong> · {shelter.name}
      </div>

      {/* Submit */}
      <button
        className={`sp-btn ${severity === 'critical' || severity === 'high' ? 'sp-btn-danger' : 'sp-btn-primary'}`}
        onClick={handleSubmit}
        disabled={saving}
        style={{ marginBottom: 24 }}
      >
        {saving ? 'Submitting…' : (
          <><IconAlertTriangle size={18} /> Submit Report</>
        )}
      </button>

      {/* Log */}
      {reports.length > 0 && (
        <div>
          <button
            onClick={() => setShowLog(v => !v)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 14, color: '#4B3FFF', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 8, padding: 0, marginBottom: 8,
            }}
          >
            <IconHistory size={14} /> {showLog ? 'Hide' : 'Show'} submitted reports ({reports.length})
          </button>

          {showLog && (
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
              {reports.slice(0, 20).map(r => {
                const s = SEVERITIES.find(sv => sv.value === r.severity)!;
                const t = INCIDENT_REPORT_TYPES.find(tp => tp.value === r.type);
                return (
                  <div key={r.id} style={{
                    padding: '16px 16px', borderBottom: '1px solid #f1f5f9',
                    display: 'flex', flexDirection: 'column', gap: 4,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>{t?.label ?? r.type}</span>
                      <span style={{
                        fontSize: 12, fontWeight: 700, padding: '2px 7px', borderRadius: 99,
                        background: s.bg, color: s.color,
                      }}>
                        {r.severity.toUpperCase()}
                      </span>
                    </div>
                    <div style={{ fontSize: 14, color: '#475569' }}>{r.description}</div>
                    {r.involvedNames && (
                      <div style={{ fontSize: 12, color: '#7B7D7F' }}>Involved: {r.involvedNames}</div>
                    )}
                    <div style={{ fontSize: 12, color: '#939598' }}>{r.timestamp} · {r.submittedBy}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
